import React, { useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CategoriesManager } from '../ui/diary/CategoriesManager';
import type { CategoriesManagerData, CategoriesManagerLabels } from '../shared/types/categories';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      refetchOnWindowFocus: false,
    },
  },
});

export interface CategoriesManagerContainerProps {
  graphqlEndpoint: string;
  csrfToken: string;
  labels: CategoriesManagerLabels;
}

const GET_CATEGORIES_QUERY = `
  query GetCategoriesManager {
    categoriesManager {
      categories {
        id
        name
        position
        editUrl
        deleteUrl
        items {
          id
          itemType
          name
          icon
          position
          editUrl
          deleteUrl
          fields {
            id
            itemType
            name
            icon
            position
            editUrl
            deleteUrl
          }
        }
      }
      scoreWidgets {
        id
        name
        position
        editUrl
        deleteUrl
        scoreFields {
          id
          name
          icon
          minValue
          maxValue
        }
      }
      newCategoryUrl
      newScoreWidgetUrl
      backUrl
    }
  }
`;

const REORDER_CATEGORIES_MUTATION = `
  mutation ReorderCategories($input: ReorderCategoriesInput!) {
    reorderCategories(input: $input) {
      success
    }
  }
`;

const REORDER_CATEGORY_ITEMS_MUTATION = `
  mutation ReorderCategoryItems($input: ReorderCategoryItemsInput!) {
    reorderCategoryItems(input: $input) {
      success
    }
  }
`;

const REORDER_FIELD_SET_FIELDS_MUTATION = `
  mutation ReorderFieldSetFields($input: ReorderFieldSetFieldsInput!) {
    reorderFieldSetFields(input: $input) {
      success
    }
  }
`;

function CategoriesManagerInner({
  graphqlEndpoint,
  csrfToken,
  labels,
}: CategoriesManagerContainerProps) {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['categoriesManager'],
    queryFn: async () => {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({ query: GET_CATEGORIES_QUERY }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch categories');
      }

      const result = await response.json();
      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'GraphQL error');
      }

      return result.data.categoriesManager as CategoriesManagerData;
    },
  });

  const reorderCategoriesMutation = useMutation({
    mutationFn: async (order: string[]) => {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: REORDER_CATEGORIES_MUTATION,
          variables: { input: { order } },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to reorder categories');
      }

      return response.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['categoriesManager'] });
    },
  });

  const reorderCategoryItemsMutation = useMutation({
    mutationFn: async ({ categoryId, order }: { categoryId: string; order: string[] }) => {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: REORDER_CATEGORY_ITEMS_MUTATION,
          variables: { input: { categoryId, order } },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to reorder items');
      }

      return response.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['categoriesManager'] });
    },
  });

  const reorderFieldSetFieldsMutation = useMutation({
    mutationFn: async ({ fieldSetId, order }: { fieldSetId: string; order: string[] }) => {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: REORDER_FIELD_SET_FIELDS_MUTATION,
          variables: { input: { fieldSetId, order } },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to reorder fields');
      }

      return response.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['categoriesManager'] });
    },
  });

  const handleReorderCategories = useCallback(
    (order: string[]) => {
      reorderCategoriesMutation.mutate(order);
    },
    [reorderCategoriesMutation]
  );

  const handleReorderCategoryItems = useCallback(
    (categoryId: string, order: string[]) => {
      reorderCategoryItemsMutation.mutate({ categoryId, order });
    },
    [reorderCategoryItemsMutation]
  );

  const handleReorderFieldSetFields = useCallback(
    (fieldSetId: string, order: string[]) => {
      reorderFieldSetFieldsMutation.mutate({ fieldSetId, order });
    },
    [reorderFieldSetFieldsMutation]
  );

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <CategoriesManager
      categories={data.categories}
      scoreWidgets={data.scoreWidgets}
      newCategoryUrl={data.newCategoryUrl}
      newScoreWidgetUrl={data.newScoreWidgetUrl}
      backUrl={data.backUrl}
      onReorderCategories={handleReorderCategories}
      onReorderCategoryItems={handleReorderCategoryItems}
      onReorderFieldSetFields={handleReorderFieldSetFields}
      labels={labels}
    />
  );
}

export function CategoriesManagerContainer(props: CategoriesManagerContainerProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <CategoriesManagerInner {...props} />
    </QueryClientProvider>
  );
}

export function mountCategoriesManager(
  container: HTMLElement,
  props: CategoriesManagerContainerProps
) {
  const existingRoot = (container as any).__reactRoot;
  if (existingRoot) {
    existingRoot.unmount();
  }

  const root = createRoot(container);
  (container as any).__reactRoot = root;
  root.render(<CategoriesManagerContainer {...props} />);
  return root;
}

if (typeof window !== 'undefined') {
  (window as any).mountCategoriesManager = mountCategoriesManager;
}

export default CategoriesManagerContainer;
